import React, { useState, useEffect, useMemo, useRef } from "react";
import VlCalendar from "./vl-calendar";
import { DAYS_SHORTCUTS } from "../../../utils/calculations";
import CalendarContext from "./CalendarContext";
import { useClickOutside } from "../../../hooks/useClickOutside";

type DayShortcut = (typeof DAYS_SHORTCUTS)[number];
type Language = "DE" | "EN";

interface VlRangeSelectorProps {
  startDate?: string | null;
  endDate?: string | null;
  minStartDate?: string | null;
  minEndDate?: string | null;
  maxStartDate?: string | null;
  maxEndDate?: string | null;
  disabledWeekdays?: number[];
  changeDate?: "start" | "end" | null;
  customClasses?: Record<string, (date: string) => boolean>;
  showWeeksNumber?: boolean;
  defaultDate?: string;
  blockStartDate?: boolean;
  disabled?: boolean;
  disabledDates?: Record<string, any> | any[];
  disabledStartDates?: Record<string, any> | any[];
  disabledEndDates?: Record<string, any> | any[];
  isDisabled?: (dateOrWeekday: string | number) => boolean;
  isOccupied?: (date: string) => boolean;
  isPartially?: (date: string) => boolean;
  singleMonth?: boolean;
  enableSingleDate?: boolean;
  firstDayOfWeek?: DayShortcut;
  occupied?: string[];
  partially?: string[];
  lang?: Language;
  onStartDateChange?: (date: string | null) => void;
  onEndDateChange?: (date: string | null) => void;
  onChangeDateChange?: (value: "start" | "end" | null) => void;
  onFocus?: (value: "focus" | "close" | null) => void;
}

export const VlRangeSelector: React.FC<VlRangeSelectorProps> = ({
  startDate = null,
  endDate = null,
  minStartDate = null,
  minEndDate = null,
  maxStartDate = null,
  maxEndDate = null,
  disabledWeekdays = [],
  changeDate = null,
  customClasses,
  showWeeksNumber = false,
  defaultDate,
  blockStartDate = false,
  disabled = false,
  disabledDates = [],
  disabledStartDates = [],
  disabledEndDates = [],
  isDisabled,
  isOccupied,
  isPartially,
  singleMonth = false,
  enableSingleDate = false,
  firstDayOfWeek = "mo",
  occupied = [],
  partially = [],
  lang = "DE",
  onStartDateChange,
  onEndDateChange,
  onChangeDateChange,
  onFocus,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // State for context
  const [selectDate, setSelectDate] = useState<"start" | "end" | null>(
    changeDate,
  );

  const [contextDisabledWeekdays, setContextDisabledWeekdays] =
    useState<number[]>(disabledWeekdays);

  // Update context when props change
  useEffect(() => {
    setSelectDate(changeDate);
  }, [changeDate]);

  useEffect(() => {
    setContextDisabledWeekdays(disabledWeekdays);
  }, [disabledWeekdays]);

  // Calculate max months
  const maxMonths = useMemo(() => {
    if (!startDate || !maxEndDate) return 12;

    const start = new Date(startDate);
    const end = new Date(maxEndDate);

    return (
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth())
    );
  }, [startDate, maxEndDate]);

  // Context value
  const contextValue = useMemo(
    () => ({
      selectDate,
      disabledWeekdays: contextDisabledWeekdays,
    }),
    [selectDate, contextDisabledWeekdays],
  );

  // Methods
  const formatDate = (date: Date): string => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const emitDate = (date: string) => {
    if (date === "changetostart") {
      onChangeDateChange?.("start");
      return;
    }
    if (date === "changetoend") {
      onChangeDateChange?.("end");
      return;
    }
    if (date == "submit") {
      onChangeDateChange?.(null);
      return;
    }
    if (blockStartDate || (startDate && !endDate && date >= startDate)) {
      onEndDateChange?.(date);
    } else {
      if (changeDate === "end") {
        if (startDate && date < startDate) {
          onStartDateChange?.(date);
          onEndDateChange?.(null);
          onChangeDateChange?.("end");
        } else {
          console.log("ENDDATE SETTER");

          onEndDateChange?.(date);
          // onChangeDateChange?.(null);
        }
      } else {
        onStartDateChange?.(date);
        onChangeDateChange?.("end");

        // Note: Original had this.maxDate which doesn't exist in props
        // Assuming this was meant to be maxEndDate
        // let maxDate = 0;
        // if (parseInt(maxEndDate) > 0) {
        //   const maxDateObj = new Date(date);
        //   maxDateObj.setDate(maxDateObj.getDate() + parseInt(maxEndDate));
        //   maxDate = formatDate(maxDateObj);
        // }

        if (endDate && endDate < date) {
          // onEndDateChange?.(null);
        }
      }
    }
    onFocus?.("focus");
  };

  const isSelected = (date: string): boolean => {
    if (!startDate) return false;

    if (!endDate) {
      return startDate === date;
    }

    return date >= startDate && date <= endDate;
  };

  const calculateOccupied = (date: string): boolean => {
    if (Array.isArray(occupied)) {
      return occupied.includes(date);
    }
    return false;
  };

  const calculatePartially = (date: string): boolean => {
    if (Array.isArray(partially)) {
      return partially.includes(date);
    }
    return false;
  };

  const calculateDisabled = (dateOrWeekday: string | number): boolean => {
    const date =
      typeof dateOrWeekday === "string" ? dateOrWeekday : dateOrWeekday;
    const minDate = changeDate === "start" ? minStartDate : minStartDate;
    const maxDate = changeDate === "start" ? maxStartDate : maxEndDate;

    // Check min/max date bounds
    if (typeof date === "string") {
      if (minDate && date < minDate) {
        return true;
      }

      if (maxDate && date > maxDate) {
        return true;
      }
    }

    const disabledDays =
      changeDate === "start"
        ? disabledStartDates || disabledDates || []
        : disabledEndDates || [];

    if (Array.isArray(disabledDays)) {
      // Check if it's a weekday (0-6)
      if (typeof dateOrWeekday === "number" && dateOrWeekday <= 6) {
        return contextDisabledWeekdays.includes(dateOrWeekday);
      } else {
        return disabledDays.includes(date);
      }
    } else {
      // Object with from/to
      const from = (disabledDays as any).from;
      const to = (disabledDays as any).to;

      const isAfterFrom = from && from <= date;
      const isBeforeTo = to && date <= to;

      if (from && to) {
        return isAfterFrom && isBeforeTo;
      }

      if (from) {
        return isAfterFrom;
      }

      if (to) {
        return isBeforeTo;
      }
    }

    return false;
  };

  const handleClickoutside = () => {
    onFocus?.("close");
  };

  useClickOutside(containerRef, handleClickoutside);

  return (
    <CalendarContext.Provider value={contextValue}>
      <div ref={containerRef}>
        <VlCalendar
          onInput={emitDate}
          isSelected={isSelected}
          isDisabled={calculateDisabled}
          isOccupied={calculateOccupied}
          isPartially={calculatePartially}
          selectedDate={{ start: startDate, end: endDate }}
          customClasses={customClasses}
          showWeeksNumber={showWeeksNumber}
          defaultDate={defaultDate}
          singleMonth={singleMonth}
          firstDayOfWeek={firstDayOfWeek}
          maxDate={changeDate === "start" ? maxStartDate : maxEndDate}
          changeDate={changeDate || "start"}
          maxMonthStart={0}
          maxMonthEnd={maxMonths}
          lang={lang}
        />
      </div>
    </CalendarContext.Provider>
  );
};

export default VlRangeSelector;
